import React, { useEffect, useState } from "react";
import { getSiteConfig } from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { Dna, Cpu, HeartPulse, Target, History, Crosshair, GraduationCap, Globe2, BookOpen } from "lucide-react";

const ICON_MAP = { Dna, Cpu, HeartPulse };

export default function AboutPage() {
  const [cfg, setCfg] = useState(null);
  const { ref, inView } = useInView({ threshold: 0.1, triggerOnce: true });

  useEffect(() => {
    getSiteConfig("about").then(r => setCfg(r.data?.data || {})).catch(() => {});
  }, []);

  if (!cfg) return <div className="min-h-screen bg-[#FAFAFA]"><Navbar /><div className="pt-32 flex justify-center"><div className="animate-spin w-8 h-8 border-3 border-[#1670B9] border-t-transparent rounded-full" /></div></div>;

  return (
    <div data-testid="about-page" className="bg-[#FAFAFA] min-h-screen">
      <Navbar />
      {/* Header */}
      <div className="bg-gradient-to-br from-[#1670B9] via-[#2d8fd4] to-[#74CDD9] pt-32 pb-20 text-center">
        <h1 className="font-['Quicksand'] text-4xl sm:text-5xl font-light text-white mb-4">À propos</h1>
        <p className="text-white/80 text-base max-w-2xl mx-auto px-6">{cfg.title || "Unité de Recherche UR-EBioReZ"}</p>
      </div>

      <div className="max-w-5xl mx-auto px-6 md:px-12 py-16" ref={ref}>
        {/* History */}
        {cfg.history && (
          <motion.section initial={{ opacity: 0, y: 20 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6 }} className="mb-16">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-[#1670B9]/10 flex items-center justify-center"><History className="w-5 h-5 text-[#1670B9]" /></div>
              <h2 className="font-['Quicksand'] text-2xl font-semibold text-slate-800">Historique</h2>
            </div>
            <p className="text-base text-slate-600 leading-relaxed pl-13">{cfg.history}</p>
          </motion.section>
        )}

        {/* Mission */}
        {cfg.mission && (
          <motion.section initial={{ opacity: 0, y: 20 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6, delay: 0.1 }} className="mb-16">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-[#74CDD9]/10 flex items-center justify-center"><Crosshair className="w-5 h-5 text-[#74CDD9]" /></div>
              <h2 className="font-['Quicksand'] text-2xl font-semibold text-slate-800">Mission</h2>
            </div>
            <div className="bg-white rounded-xl p-6 border border-slate-100 shadow-sm">
              <p className="text-base text-slate-600 leading-relaxed">{cfg.mission}</p>
            </div>
          </motion.section>
        )}

        {/* General Objective */}
        {cfg.general_objective && (
          <motion.section initial={{ opacity: 0, y: 20 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6, delay: 0.15 }} className="mb-16">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-[#F49C03]/10 flex items-center justify-center"><Target className="w-5 h-5 text-[#F49C03]" /></div>
              <h2 className="font-['Quicksand'] text-2xl font-semibold text-slate-800">Objectif général</h2>
            </div>
            <p className="text-base text-slate-600 leading-relaxed bg-gradient-to-r from-[#1670B9]/5 to-transparent rounded-xl p-6 border-l-4 border-[#1670B9]">{cfg.general_objective}</p>
          </motion.section>
        )}

        {/* Specific Objectives */}
        {cfg.specific_objectives?.length > 0 && (
          <motion.section initial={{ opacity: 0, y: 20 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6, delay: 0.2 }} className="mb-16">
            <h2 className="font-['Quicksand'] text-2xl font-semibold text-slate-800 mb-6">Objectifs spécifiques</h2>
            <div className="grid md:grid-cols-2 gap-4">
              {cfg.specific_objectives.map((obj, i) => (
                <div key={`obj-${obj.slice(0,15)}-${i}`} className="flex items-start gap-3 bg-white rounded-xl p-4 border border-slate-100">
                  <div className="w-7 h-7 rounded-full bg-[#1670B9] flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs font-bold text-white">{i + 1}</span>
                  </div>
                  <p className="text-sm text-slate-600 leading-relaxed">{obj}</p>
                </div>
              ))}
            </div>
          </motion.section>
        )}

        {/* Domains */}
        {cfg.domains?.length > 0 && (
          <motion.section initial={{ opacity: 0, y: 20 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6, delay: 0.25 }} className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-[#1670B9]/10 flex items-center justify-center"><Globe2 className="w-5 h-5 text-[#1670B9]" /></div>
              <h2 className="font-['Quicksand'] text-2xl font-semibold text-slate-800">Domaines d'intervention</h2>
            </div>
            <div className="flex flex-wrap gap-3">
              {cfg.domains.map((d, i) => (
                <span key={`dom-${d.slice(0,15)}-${i}`} className="px-4 py-2 rounded-xl bg-white border border-slate-100 text-sm text-slate-700 font-medium shadow-sm">{d}</span>
              ))}
            </div>
          </motion.section>
        )}

        {/* Research Axes - Features */}
        {cfg.features?.length > 0 && (
          <motion.section initial={{ opacity: 0, y: 20 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6, delay: 0.3 }} className="mb-16">
            <h2 className="font-['Quicksand'] text-2xl font-semibold text-slate-800 mb-6">Axes de recherche</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {cfg.features.map((f, i) => {
                const IconComp = ICON_MAP[f.icon] || Dna;
                return (
                  <div key={`feat-${f.title}-${i}`} className="bg-white rounded-xl p-6 border border-slate-100 hover:shadow-md transition-shadow">
                    <div className="w-12 h-12 rounded-xl bg-[#1670B9]/10 flex items-center justify-center mb-4"><IconComp className="w-6 h-6 text-[#1670B9]" /></div>
                    <h3 className="font-['Quicksand'] text-base font-semibold text-slate-800 mb-2">{f.title}</h3>
                    <p className="text-sm text-slate-500 leading-relaxed">{f.desc}</p>
                  </div>
                );
              })}
            </div>
          </motion.section>
        )}

        {/* Formation */}
        {cfg.formation && (
          <motion.section initial={{ opacity: 0, y: 20 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6, delay: 0.35 }} className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-[#74CDD9]/10 flex items-center justify-center"><GraduationCap className="w-5 h-5 text-[#74CDD9]" /></div>
              <h2 className="font-['Quicksand'] text-2xl font-semibold text-slate-800">Formation</h2>
            </div>
            <p className="text-base text-slate-600 leading-relaxed">{cfg.formation}</p>
          </motion.section>
        )}
      </div>
      <Footer />
    </div>
  );
}
